import React, { ChangeEvent, useEffect, useMemo } from 'react';

import { WebsiteTestimonial } from '@flipdish/api-client-typescript';
import { FormControl, FormHelperText } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import RemoveIcon from '@material-ui/icons/ClearOutlined';
import { Field, FieldProps, FormikProps, withFormik } from 'formik';
import { debounce } from 'lodash';
import { Translate } from 'react-localize-redux';

import { formikValidate } from '../../../helpers/validation';
import TextField from '../../../ui/TextField/TextField';

export type Testimonial = WebsiteTestimonial & {
  fakeId?: number;
};

const useStyles = makeStyles({
  fieldWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
  },
  textAreaWrapper: {
    flexGrow: 1,
    '& textarea': {
      paddingRight: 44,
    },
  },
  remove: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
});

type FormValues = ReturnType<typeof mapPropsToValues>;

const mapPropsToValues = ({ data }: Props) => {
  return data;
};

type Props = {
  data: Testimonial;
  index: number;
  disabled: boolean;
  onChange: (data: FormValues) => void;
  onRemove: (id: number) => void;
};

const validate = (value: string) => {
  return formikValidate.required(value) || formikValidate.containsHTMLElement(value);
};

export default withFormik<Props, FormValues>({
  displayName: 'EditWebsiteSettingsAboutSectionItem',
  mapPropsToValues,
  handleSubmit: (values, formikBag) => {
    formikBag.props.onChange(values);
  },
})((props: Props & FormikProps<FormValues>) => {
  const classes = useStyles();
  const { errors, submitForm, onRemove, data, index, disabled } = props;

  const save = useMemo(() => {
    return debounce(() => {
      submitForm();
    }, 500);
  }, []);

  useEffect(() => {
    submitForm();
  }, []);

  return (
    <div>
      <Field name="Message" validate={validate}>
        {(fProps: FieldProps) => {
          const { field, form } = fProps;
          return (
            <FormControl fullWidth className={classes.fieldWrapper}>
              <TextField
                className={classes.textAreaWrapper}
                fdKey={`testimonial_content_${index}`}
                value={field.value}
                disabled={disabled}
                label={
                  <span>
                    <Translate id="Testimonial" /> #{index + 1}
                  </span>
                }
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  form.setFieldValue(field.name, e.target.value);
                  save();
                }}
              />
              <span
                data-fd={`remove_testimonial_${index}`}
                className={classes.remove}
                onClick={() => {
                  const id = data.TestimonialId || data.fakeId;

                  if (!disabled && id) {
                    onRemove(id);
                  }
                }}
              >
                <RemoveIcon />
              </span>
            </FormControl>
          );
        }}
      </Field>
      {errors.Message ? (
        <FormControl error fullWidth>
          <FormHelperText>{errors.Message}</FormHelperText>
        </FormControl>
      ) : null}
    </div>
  );
});
